import { Panel, InlineWarning } from "@/components/common/Primitives";
import { num } from "@/lib/format";

const TONE = {
  HIGH: "border-red-300 bg-red-50 text-red-800",
  MEDIUM: "border-amber-300 bg-amber-50 text-amber-800",
  INFO: "border-blue-300 bg-blue-50 text-blue-800",
};

const inputCls =
  "h-7 w-20 rounded-sm border border-slate-300 bg-white px-2 text-right text-[12px] outline-none focus:border-blue-500";

export const RiskSettingsPanel = ({ catalog = [], config = {}, onChange }) => {
  const penalties = config.penalties || {};
  const set = (patch) => onChange({ ...config, ...patch });

  return (
    <Panel
      title="Risico-aftrek per risicosoort"
      subtitle="Bepaalt hoeveel scorepunten een risico kost. Eén onderliggend risico wordt altijd maar één keer verrekend."
      testid="settings-risk-panel"
    >
      <div className="grid gap-3 border-b border-slate-200 p-3 md:grid-cols-2">
        <div>
          <label className="label-xs">Maximale totale risico-aftrek (punten)</label>
          <input
            type="number"
            step="0.5"
            value={config.max_flag_penalty ?? 10}
            data-testid="risk-max-penalty"
            onChange={(e) => set({ max_flag_penalty: Number(e.target.value) })}
            className="mt-1 h-8 w-full rounded-sm border border-slate-300 px-2 text-[12.5px]"
          />
          <p className="mt-0.5 text-[10.5px] text-slate-500">
            De risicocomponent weegt 10 van de 100 punten in de Deal Score.
          </p>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-[12px] font-medium text-slate-700">
            <input
              type="checkbox"
              checked={!!config.hide_high_risk}
              data-testid="risk-hide-high"
              onChange={(e) => set({ hide_high_risk: e.target.checked })}
              className="h-3.5 w-3.5 accent-blue-600"
            />
            Auto's met hoog risico standaard verbergen in Handelskansen
          </label>
        </div>
      </div>

      <div className="p-3">
        <InlineWarning tone="info">
          Een hoge aftrek verlaagt de Deal Score, maar verbergt een auto nooit automatisch: een
          schadeauto of US-import kan juist interessant zijn bij de juiste prijs.
        </InlineWarning>
      </div>

      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Ernst</th>
              <th>Risicosoort</th>
              <th>Groep</th>
              <th className="text-right">Zoektermen</th>
              <th className="text-right">Standaard</th>
              <th className="text-right">Aftrek (punten)</th>
              <th className="text-right">Auto's nu</th>
            </tr>
          </thead>
          <tbody>
            {catalog.map((rule) => (
              <tr key={rule.code} data-testid={`risk-rule-${rule.code}`}>
                <td>
                  <span className={`chip ${TONE[rule.severity] || TONE.INFO}`}>
                    {rule.severity === "HIGH"
                      ? "Hoog"
                      : rule.severity === "MEDIUM"
                        ? "Middel"
                        : "Info"}
                  </span>
                </td>
                <td className="text-[12px] font-semibold text-slate-800">{rule.label}</td>
                <td className="text-[10.5px] text-slate-500">{rule.group}</td>
                <td className="num text-[11px]">{num(rule.term_count)}</td>
                <td className="num text-[11px] text-slate-500">
                  {num(rule.default_penalty, { decimals: 1 })}
                </td>
                <td className="text-right">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={penalties[rule.code] ?? rule.default_penalty}
                    data-testid={`risk-penalty-${rule.code}`}
                    onChange={(e) =>
                      set({
                        penalties: { ...penalties, [rule.code]: Number(e.target.value) },
                      })
                    }
                    className={inputCls}
                  />
                </td>
                <td className="num text-[11px]">{num(rule.vehicles)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-3 py-2 text-[10.5px] leading-snug text-slate-500">
        Na het opslaan van gewijzigde aftrekken moet je één keer herberekenen (Instellingen →
        Databronnen → herberekenen, of de knop op de marktdatapagina) zodat alle Deal Scores de
        nieuwe weging gebruiken.
      </p>
    </Panel>
  );
};

export default RiskSettingsPanel;
